import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard, Building2, TrendingUp, TrendingDown, Users,
  Wallet, CreditCard, BarChart3, Settings, LogOut, X, Repeat,
  Globe, UserPlus, ChevronDown, ChevronRight, Briefcase, RefreshCw, ShieldCheck
} from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import { ScrollArea } from '../ui/scroll-area';
import { affiliateCompaniesAPI, affiliateBrandsAPI } from '../../lib/api';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/companies', label: 'Companies', icon: Building2 },
  { path: '/partners', label: 'Partners', icon: UserPlus },
  { type: 'affiliate' },
  { path: '/incomes', label: 'Incomes', icon: TrendingUp },
  { path: '/expenses', label: 'Expenses', icon: TrendingDown },
  { path: '/personnel', label: 'Personnel', icon: Users },
  { path: '/subscriptions', label: 'Subscriptions', icon: Repeat },
  { path: '/wallets', label: 'Wallets', icon: Wallet },
  { path: '/transactions', label: 'Transactions', icon: CreditCard },
  { path: '/reports', label: 'Reports', icon: BarChart3 },
  { path: '/update', label: 'Update', icon: RefreshCw },
  { path: '/users', label: 'Users', icon: ShieldCheck, adminOnly: true },
  { path: '/settings', label: 'Settings', icon: Settings },
];

export function Sidebar({ open, onClose }) {
  const location = useLocation();
  const { logout, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [companies, setCompanies] = useState([]);
  const [brands, setBrands] = useState([]);
  const [affiliateOpen, setAffiliateOpen] = useState(false);
  const [expandedCompanies, setExpandedCompanies] = useState({});

  useEffect(() => {
    const loadData = () => {
      affiliateCompaniesAPI.getAll().then(res => setCompanies(res.data)).catch(() => {});
      affiliateBrandsAPI.getAll().then(res => setBrands(res.data)).catch(() => {});
    };
    loadData();
    window.addEventListener('affiliate-companies-changed', loadData);
    return () => window.removeEventListener('affiliate-companies-changed', loadData);
  }, []);

  // Auto-expand if on an affiliate page
  useEffect(() => {
    if (location.pathname.startsWith('/affiliate')) {
      setAffiliateOpen(true);
      // Auto-expand active company
      const match = location.pathname.match(/\/affiliate\/company\/(.+)/);
      if (match) {
        setExpandedCompanies(prev => ({ ...prev, [match[1]]: true }));
      }
    }
  }, [location.pathname]);

  const toggleCompany = (companyId) => {
    setExpandedCompanies(prev => ({ ...prev, [companyId]: !prev[companyId] }));
  };

  const getBrandsForCompany = (companyId) => brands.filter(b => b.company_id === companyId);

  const isAffiliateActive = location.pathname.startsWith('/affiliate');

  return (
    <>
      {open && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} />
      )}
      <aside className={cn(
        "fixed top-0 left-0 z-50 h-screen w-64 bg-card border-r border-border",
        "transform transition-transform duration-300 ease-in-out",
        "lg:translate-x-0 lg:static lg:z-auto",
        open ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between h-16 px-6 border-b border-border">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-heading font-bold text-lg">F</span>
              </div>
              <span className="font-heading font-bold text-xl">FinNexus</span>
            </div>
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {navItems.map((item, idx) => {
                if (item.type === 'affiliate') {
                  return (
                    <div key="affiliate-section">
                      {/* Affiliate Main Button */}
                      <div className="flex items-center">
                        <NavLink
                          to="/affiliate"
                          onClick={onClose}
                          className={cn(
                            "flex-1 flex items-center gap-3 px-3 py-2.5 rounded-l-lg text-sm font-medium transition-all duration-200",
                            isAffiliateActive
                              ? "bg-primary text-primary-foreground shadow-glow-sm"
                              : "text-muted-foreground hover:text-foreground hover:bg-accent"
                          )}
                        >
                          <Globe className="h-5 w-5" />
                          Affiliate
                        </NavLink>
                        <button
                          onClick={() => setAffiliateOpen(!affiliateOpen)}
                          className={cn(
                            "px-2 py-2.5 rounded-r-lg text-sm transition-all duration-200",
                            isAffiliateActive
                              ? "bg-primary text-primary-foreground"
                              : "text-muted-foreground hover:text-foreground hover:bg-accent"
                          )}
                        >
                          {affiliateOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      </div>

                      {/* Company Sub-items with Brands */}
                      {affiliateOpen && (
                        <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-border/50 pl-3">
                          {companies.map((company) => {
                            const companyBrands = getBrandsForCompany(company.id);
                            const isExpanded = expandedCompanies[company.id];
                            const isCompanyActive = location.pathname === `/affiliate/company/${company.id}`;

                            return (
                              <div key={company.id}>
                                <div className="flex items-center">
                                  <NavLink
                                    to={`/affiliate/company/${company.id}`}
                                    onClick={onClose}
                                    className={cn(
                                      "flex-1 flex items-center gap-2 px-3 py-2 rounded-l-lg text-xs font-medium transition-all duration-200",
                                      isCompanyActive
                                        ? "bg-primary/20 text-primary"
                                        : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                                    )}
                                    data-testid={`sidebar-company-${company.id}`}
                                  >
                                    <Briefcase className="h-3.5 w-3.5 shrink-0" />
                                    <span className="truncate">{company.name}</span>
                                  </NavLink>
                                  {companyBrands.length > 0 && (
                                    <button
                                      onClick={() => toggleCompany(company.id)}
                                      className={cn(
                                        "px-1.5 py-2 rounded-r-lg text-xs transition-all duration-200",
                                        isCompanyActive
                                          ? "text-primary hover:bg-primary/10"
                                          : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                                      )}
                                      data-testid={`sidebar-toggle-${company.id}`}
                                    >
                                      {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                    </button>
                                  )}
                                  {companyBrands.length === 0 && (
                                    <span className="px-1.5 py-2 text-[10px] text-muted-foreground/40">0</span>
                                  )}
                                </div>

                                {/* Brand sub-items */}
                                {isExpanded && companyBrands.length > 0 && (
                                  <div className="ml-3 mt-0.5 space-y-0.5 border-l border-border/30 pl-2.5">
                                    {companyBrands.map(brand => (
                                      <NavLink
                                        key={brand.id}
                                        to={`/affiliate/company/${company.id}`}
                                        onClick={onClose}
                                        className="flex items-center gap-1.5 px-2 py-1.5 rounded-md text-[11px] text-muted-foreground hover:text-foreground hover:bg-accent/30 transition-all duration-200"
                                        data-testid={`sidebar-brand-${brand.id}`}
                                      >
                                        <Globe className="h-3 w-3 shrink-0 text-primary/60" />
                                        <span className="truncate">{brand.name}</span>
                                        {brand.provider && (
                                          <span className="ml-auto text-[9px] bg-muted/60 px-1 py-0.5 rounded shrink-0">{brand.provider.slice(0, 3).toUpperCase()}</span>
                                        )}
                                      </NavLink>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {companies.length === 0 && (
                            <p className="px-3 py-2 text-xs text-muted-foreground/60">No companies yet</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                }

                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                if (item.adminOnly && !isAdmin) return null;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={onClose}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
                      isActive
                        ? "bg-primary text-primary-foreground shadow-glow-sm"
                        : "text-muted-foreground hover:text-foreground hover:bg-accent"
                    )}
                  >
                    <Icon className="h-5 w-5" />
                    {item.label}
                  </NavLink>
                );
              })}
            </nav>
          </ScrollArea>

          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-primary font-medium">{user?.full_name?.charAt(0)?.toUpperCase() || 'U'}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
            <Button variant="ghost" className="w-full justify-start text-muted-foreground hover:text-destructive" onClick={logout} data-testid="logout-btn">
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
}
